/**
 * Sidebar Module
 * Handles plant filtering, seasonal recommendations, and zone-based suggestions
 */

// Month-based planting recommendations
const monthlyPlantingGuide = {
    january: ['Onion', 'Garlic', 'Leek', 'Asparagus', 'Shallots', 'Radish'],
    february: ['Onion', 'Peas', 'Potato', 'Spinach', 'Kale', 'Lettuce'],
    march: ['Beet', 'Broccoli', 'Cabbage', 'Carrot', 'Lettuce', 'Peas', 'Spinach'],
    april: ['Beans', 'Cucumber', 'Corn', 'Tomato', 'Zucchini', 'Bell Pepper'],
    may: ['Beans', 'Corn', 'Cucumber', 'Eggplant', 'Okra', 'Pumpkin', 'Squash', 'Sweet Potato'],
    june: ['Beans', 'Corn', 'Cucumber', 'Pumpkin', 'Watermelon', 'Cantaloupe'],
    july: ['Beans', 'Corn', 'Cucumber', 'Kale', 'Lettuce', 'Spinach'],
    august: ['Beet', 'Broccoli', 'Kale', 'Lettuce', 'Spinach', 'Radish'],
    september: ['Beet', 'Broccoli', 'Cabbage', 'Kale', 'Lettuce', 'Spinach'],
    october: ['Garlic', 'Onion', 'Leek', 'Shallots', 'Radish', 'Spinach'],
    november: ['Garlic', 'Onion', 'Shallots', 'Spinach'],
    december: ['Garlic', 'Asparagus', 'Onion', 'Shallots']
};

// Planting zones with recommended plants
const plantingZones = {
    zone1: {
        name: "Zone 1 (Below -50°F)",
        plants: ['Garlic', 'Onion', 'Kale', 'Spinach', 'Lettuce']
    },
    zone2: {
        name: "Zone 2 (-50°F to -40°F)",
        plants: ['Garlic', 'Onion', 'Kale', 'Spinach', 'Lettuce', 'Radish', 'Peas']
    },
    zone3: {
        name: "Zone 3 (-40°F to -30°F)",
        plants: ['Garlic', 'Onion', 'Kale', 'Spinach', 'Lettuce', 'Peas', 'Potato', 'Radish', 'Cabbage']
    },
    zone4: {
        name: "Zone 4 (-30°F to -20°F)",
        plants: ['Garlic', 'Onion', 'Kale', 'Spinach', 'Lettuce', 'Peas', 'Potato', 'Beet', 'Carrot', 'Broccoli']
    },
    zone5: {
        name: "Zone 5 (-20°F to -10°F)",
        plants: ['Tomato', 'Pepper', 'Beans', 'Corn', 'Cucumber', 'Squash', 'Pumpkin', 'Zucchini', 'Broccoli', 'Cabbage']
    },
    zone6: {
        name: "Zone 6 (-10°F to 0°F)",
        plants: ['Tomato', 'Pepper', 'Beans', 'Corn', 'Cucumber', 'Squash', 'Eggplant', 'Watermelon', 'Cantaloupe']
    },
    zone7: {
        name: "Zone 7 (0°F to 10°F)",
        plants: ['Tomato', 'Pepper', 'Eggplant', 'Okra', 'Beans', 'Corn', 'Cucumber', 'Squash', 'Watermelon']
    },
    zone8: {
        name: "Zone 8 (10°F to 20°F)",
        plants: ['Tomato', 'Pepper', 'Eggplant', 'Okra', 'Sweet Potato', 'Watermelon', 'Cantaloupe', 'Pumpkin']
    },
    zone9: {
        name: "Zone 9 (20°F to 30°F)",
        plants: ['Tomato', 'Pepper', 'Eggplant', 'Okra', 'Sweet Potato', 'Watermelon', 'Cantaloupe', 'Citrus']
    },
    zone10: {
        name: "Zone 10 (30°F to 40°F)",
        plants: ['Tomato', 'Pepper', 'Eggplant', 'Okra', 'Sweet Potato', 'Tropical Fruits']
    },
    zone11: {
        name: "Zone 11 (40°F to 50°F)",
        plants: ['Tomato', 'Pepper', 'Eggplant', 'Okra', 'Sweet Potato', 'Tropical Fruits', 'Pineapple', 'Mango']
    },
    zone12: {
        name: "Zone 12 (50°F to 60°F)",
        plants: ['Tomato', 'Pepper', 'Eggplant', 'Okra', 'Sweet Potato', 'Tropical Fruits', 'Pineapple', 'Mango', 'Banana', 'Papaya']
    },
    zone13: {
        name: "Zone 13 (60°F to 70°F)",
        plants: ['Tropical Fruits', 'Pineapple', 'Mango', 'Banana', 'Papaya', 'Coconut', 'Guava', 'Passion Fruit']
    }
};

function filterPlants() {
    const checkedCategories = Array.from(document.querySelectorAll('.filter-checkbox:checked')).map(cb => cb.value);
    const plantButtons = document.querySelectorAll('.plantButton-container .grid-item');
    
    // If no categories selected, show all plants
    if (checkedCategories.length === 0) {
        plantButtons.forEach(button => {
            button.style.display = 'flex';
        });
        return;
    }
    
    // Show only plants from selected categories
    plantButtons.forEach(button => {
        const plantName = button.textContent;
        let shouldShow = false;
        
        checkedCategories.forEach(category => {
            if (window.plantCategories && window.plantCategories[category] && window.plantCategories[category].includes(plantName)) {
                shouldShow = true;
            }
        });
        
        button.style.display = shouldShow ? 'flex' : 'none';
    });
}

// Function to update plant highlighting based on all active filters
function updatePlantHighlighting() {
    const season = document.getElementById('season-select')?.value;
    const month = document.getElementById('month-select')?.value;
    const zone = document.getElementById('zone-select')?.value;
    
    const seasonPlants = season ? {
        spring: ['Lettuce', 'Pea', 'Radish', 'Carrot', 'Broccoli'],
        summer: ['Tomato', 'Cucumber', 'Bell Pepper', 'Corn', 'Eggplant'],
        fall: ['Kale', 'Spinach', 'Cabbage', 'Brussels Sprout', 'Beet'],
        winter: ['Garlic', 'Onion', 'Collard', 'Turnip', 'Leek']
    }[season] : [];
    
    const monthPlants = month ? monthlyPlantingGuide[month] || [] : [];
    const zonePlants = zone ? plantingZones[zone]?.plants || [] : [];
    
    // Reset all plant button styles
    const plantButtons = document.querySelectorAll('.plantButton-container .grid-item');
    plantButtons.forEach(button => {
        button.classList.remove('season-recommended', 'month-recommended', 'zone-recommended', 'multi-recommended');
    });
    
    // Apply highlighting based on recommendations
    plantButtons.forEach(button => {
        const plantName = button.textContent;
        const recommendations = [];
        
        if (seasonPlants.includes(plantName)) recommendations.push('season');
        if (monthPlants.includes(plantName)) recommendations.push('month');
        if (zonePlants.includes(plantName)) recommendations.push('zone');
        
        if (recommendations.length > 1) {
            button.classList.add('multi-recommended');
        } else if (recommendations.length === 1) {
            button.classList.add(`${recommendations[0]}-recommended`);
        }
    });
}

export function initializeSidebar() {
    // Add CSS classes for recommended plants
    const style = document.createElement('style');
    style.textContent = `
        .season-recommended {
            border: 2px solid rgb(255, 184, 53);
            background-color: rgb(255, 173, 251);
        }
        
        .month-recommended {
            border: 2px solid rgb(0, 128, 0);
            background-color: rgb(200, 255, 200);
        }
        
        .zone-recommended {
            border: 2px solid rgb(0, 0, 255);
            background-color: rgb(200, 200, 255);
        }
        
        .multi-recommended {
            border: 3px solid rgb(128, 0, 128);
            background-color: rgb(230, 200, 230);
        }
    `;
    document.head.appendChild(style);

    // Event listeners for category filters
    document.querySelectorAll('.filter-checkbox').forEach(checkbox => {
        checkbox.addEventListener('change', filterPlants);
    });

    // Handle season selection
    const seasonSelect = document.getElementById('season-select');
    if (seasonSelect) {
        seasonSelect.addEventListener('change', function() {
            const season = this.value;
            const seasonInfo = document.getElementById('season-info');
            
            // Define recommended plants for each season
            const seasonPlants = {
                spring: ['Lettuce', 'Pea', 'Radish', 'Carrot', 'Broccoli'],
                summer: ['Tomato', 'Cucumber', 'Bell Pepper', 'Corn', 'Eggplant'],
                fall: ['Kale', 'Spinach', 'Cabbage', 'Brussels Sprout', 'Beet'],
                winter: ['Garlic', 'Onion', 'Collard', 'Turnip', 'Leek']
            };
            
            if (season === "" || !seasonInfo) {
                if (seasonInfo) seasonInfo.innerHTML = "";
                updatePlantHighlighting();
                return;
            }

            // Update the season info box
            seasonInfo.innerHTML = `
                <p><strong>${season.charAt(0).toUpperCase() + season.slice(1)} Recommendations:</strong></p>
                <ul>
                    ${seasonPlants[season].map(plant => `<li>${plant}</li>`).join('')}
                </ul>
            `;
            
            updatePlantHighlighting();
        });
    }

    // Handle month selection
    const monthSelect = document.getElementById('month-select');
    if (monthSelect) {
        monthSelect.addEventListener('change', function() {
            const month = this.value;
            const monthInfo = document.getElementById('month-info');
            
            if (month === "" || !monthInfo) {
                if (monthInfo) monthInfo.innerHTML = "";
                updatePlantHighlighting();
                return;
            }
            
            // Update the month info box
            monthInfo.innerHTML = `
                <p><strong>${month.charAt(0).toUpperCase() + month.slice(1)} Planting Recommendations:</strong></p>
                <ul>
                    ${monthlyPlantingGuide[month].map(plant => `<li>${plant}</li>`).join('')}
                </ul>
            `;
            
            updatePlantHighlighting();
        });
    }
    
    console.log('✅ Sidebar functionality initialized');
}